import React, { FC, InputHTMLAttributes, useState, useEffect, useRef } from 'react';
import NumberInput from '../numberInput';

interface EmptyNumberInputProps<T> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onBlur'> {
  formValueState: [T, React.Dispatch<React.SetStateAction<T>>];
  emptyValueState: [T, React.Dispatch<React.SetStateAction<T>>];
  'data-key': number[];
  'data-sign': number[];
  rowIndex?: number;
  colIndex: number;
  className?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>, previousValue: number) => void;
  mathCalc?: (value: number) => number;
  validateChange?: (before: number, after: number) => boolean;
  significantDigits?: number;
}

function isNumeric(value: string): boolean {
  return /^-?[0-9]+(\.[0-9]+)?$/.test(value.trim());
}

const EmptyNumberInput: FC<EmptyNumberInputProps<any>> = (props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const focusValueRef = useRef<number | null>(null);
  const [formValues, setFormValues] = props.formValueState;
  const signKeys = props['data-sign'];
  const _signIndex = signKeys[0];
  const signIndex = typeof _signIndex === 'undefined' ? -1 : _signIndex;
  const propSignKey = Object.keys(formValues)[signIndex];
  const formSimpleState = useState(formValues[propSignKey][props.colIndex]);
  const [formSimpleValues, setSimpleFormValues] = formSimpleState;
  const [emptyValues, setEmptyValues] = props.emptyValueState;
  const keys = props['data-key'];
  const _index = keys[0];
  const index = typeof _index === 'undefined' ? -1 : _index;
  const propKey: string = Object.keys(emptyValues[propSignKey][0])[index];

  useEffect(() => {
    setSimpleFormValues(formValues[propSignKey][props.colIndex]);
  }, [formValues, propSignKey, props.colIndex]);

  const getCurrentValue = (): number =>
    typeof props.rowIndex === 'number' ? formValues[propSignKey][props.colIndex][propKey][props.rowIndex] : formValues[propSignKey][props.colIndex][propKey];

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    focusValueRef.current = getCurrentValue();
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    //再レンダリングさせる
    const tempEmptyValues = [...emptyValues[propSignKey]];
    //空白セルチェック
    const beforeEmpty: boolean = typeof props.rowIndex === 'number' ? tempEmptyValues[props.colIndex][propKey][props.rowIndex] : tempEmptyValues[props.colIndex][propKey];
    const inputValue: string = e.target.value;
    const afterValue: number = Number(inputValue);
    const tempSimpleValues = { ...formSimpleValues };
    const tempValues = { ...formValues };
    const beforeValue: number = focusValueRef.current ?? getCurrentValue();
    // 空白表示中は入力範囲チェックは行わない
    const isValidInput: boolean =
      isNumeric(inputValue) && (beforeEmpty || props.validateChange === undefined || (props.validateChange !== undefined && props.validateChange(beforeValue, afterValue)));
    // 入力範囲外の値ならば、入力前の値に戻す
    const fixValue = Number((isValidInput ? afterValue : beforeValue).toFixed(props.significantDigits ?? 0));
    if (typeof props.rowIndex === 'number') {
      tempSimpleValues[propKey][props.rowIndex] = fixValue;
      tempValues[propSignKey][props.colIndex][propKey][props.rowIndex] = fixValue;
    } else {
      tempSimpleValues[propKey] = fixValue;
      tempValues[propSignKey][props.colIndex][propKey] = fixValue;
    }
    setSimpleFormValues(tempSimpleValues);
    setFormValues(tempValues);

    if (beforeEmpty) {
      if (isValidInput) {
        // 入力が正常ならば、空白表示を解除する
        if (typeof props.rowIndex === 'number') {
          tempEmptyValues[props.colIndex][propKey][props.rowIndex] = false;
        } else {
          tempEmptyValues[props.colIndex][propKey] = false;
        }
        setEmptyValues(() => {
          const stateClone = structuredClone(emptyValues); // 深いクローン
          stateClone[propSignKey] = tempEmptyValues;
          return stateClone;
        });
      } else {
        // 空白表示中に行われた不正な入力をクリア
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    }

    if (props.onBlur) {
      props.onBlur(e, beforeValue);
    }
    focusValueRef.current = null;
  };

  if (
    (typeof props.rowIndex === 'number' && emptyValues[propSignKey][props.colIndex][propKey][props.rowIndex]) ||
    (typeof props.rowIndex !== 'number' && emptyValues[propSignKey][props.colIndex][propKey])
  ) {
    // 空白表示
    return <input ref={inputRef} className={props.className} onFocus={onFocus} onBlur={onBlur} />;
  } else {
    return (
      <NumberInput
        className={props.className}
        data-key={typeof props.rowIndex === 'number' ? [props['data-key'][0], props.rowIndex] : [props['data-key'][0]]}
        formValueState={formSimpleState}
        value={typeof props.rowIndex === 'number' ? formSimpleValues[propKey][props.rowIndex] : formSimpleValues[propKey]}
        onFocus={onFocus}
        onBlur={onBlur}
        mathCalc={props.mathCalc}
        data-significant-digits={String(props.significantDigits)}
      />
    );
  }
};

export default EmptyNumberInput;
